import { Injectable } from '@angular/core';
import { Observable, map, mergeMap } from 'rxjs';
import { ApiResponse } from '../../models/api-response.model';
import { BlobFileSasUrl } from '../../models/blob-file-sas-url.model';
import { CustomerAckRejectionReason } from '../../models/customer-ack-rejection-reason.model';
import { CustomerAcknowledgement } from '../../models/customer-ack.model';
import { CustomerAcknowledgementDocumentModel } from '../../models/customer-acknowledgement-document.model';
import { CustomerAcknowledgementStatus } from '../../models/customer-acknowledgement-status.model';
import { CustomerSurveyPreviewEventDetails } from '../../models/customer-survey-preview-event-details.model';
import { CustomerSurveyResponse } from '../../models/customer-survey-response.model';
import { CustomerSurveySubmissionModel } from '../../models/customer-survey-submission.model';
import { CustomerSurveyModel } from '../../models/customer-survey.,model';
import { CustomerValidationInfo } from '../../models/customer-validation-info.model';
import { CustomerValidationPreviewEventDetails } from '../../models/customer-validation-preview-event-details.model';
import { DeclineReason } from '../../models/decline-reason.model';
import { EventCustomerAttendee } from '../../models/event-customer-attendee-dropdown.model';
import { EventCustomerRejectionReason } from '../../models/event-customer-rejection-reason.model';
import { EventCustomerValidationRequestModel } from '../../models/event-customer-validation-request.model';
import {
  EventCustomerValidationModel,
  EventCustomerValidationStatusDataItem
} from '../../models/event-customer-validation.model';
import { EventCustomers } from '../../models/event-customers.model';
import { EventDetails } from '../../models/event-detail.model';
import { EventDocumentRejectionReason } from '../../models/event-document-rejection-reason.model';
import { EventL1InternalStatus } from '../../models/event-l1-internal-status.model';
import { EventL2InternalStatus } from '../../models/event-l2-internal-status.model';
import { EventPmoNextSteps } from '../../models/event-pmo-next-steps.model';
import { EventPoeDocument } from '../../models/event-poe-document.model';
import { EventPOEInfoModel } from '../../models/event-poe-info.model';
import { EventPreviewDetails } from '../../models/event-preview-details.model';
import {
  EventRegistrationDocumentModel,
  POEDocument,
  ProgramDocument
} from '../../models/event-registration-document.model';
import { EventRequiredCustomerLimit } from '../../models/event-required-customer-attendee-limit.model';
import { EventSurveyRejectReason } from '../../models/event-survey-reject-reason.model';
import { EventTaskOwner } from '../../models/event-task-owner.model';
import { MilestonePaymentStatus } from '../../models/milestone-payment-status.model';
import { SurveyDetails } from '../../models/survey-details.model';
import { UpdateCustomerAckDocumentsStatusRequest } from '../../models/update-customer-ack-document-status-request.model';
import { UpdateEventDocumentsStatusRequest } from '../../models/update-event-documents-status-request.model';
import { EventsApiBase } from '../mcap-events/api-base';
import { EventDocumentStatus } from '../../models/event-document-status.model';
import { EventPOEStatus } from '../../models/event-poe-status.model';
import { EventSurveyStatus } from '../../models/event-survey-status.model';

@Injectable({
  providedIn: 'root'
})
export class EventEditService extends EventsApiBase {
  getEventSurveyStatusDropDown(): Observable<EventSurveyStatus[]> {
    return this.get<EventSurveyStatus[]>(
      'api/EventDetails/GetSurveyStatus',
      {}
    ) as Observable<EventSurveyStatus[]>;
  }

  getEventSurveyRejectionReasonDropDown(): Observable<
    EventSurveyRejectReason[]
  > {
    return this.get<EventSurveyRejectReason[]>(
      'api/EventDetails/GetSurveyRejectionReasons',
      {}
    ) as Observable<EventSurveyRejectReason[]>;
  }

  getEventCustomerSurveyList(customerId): Observable<CustomerSurveyModel[]> {
    return this.get<CustomerSurveyModel[]>(
      'api/EventDetails/GetCustomerSurveys/' + customerId,
      {}
    ) as Observable<CustomerSurveyModel[]>;
  }
  getCustomerSurveyResponse(
    requestId: number
  ): Observable<CustomerSurveyResponse> {
    return this.get<CustomerSurveyResponse>(
      'api/Survey/GetAllResponses/' + requestId,
      {}
    ) as Observable<CustomerSurveyResponse>;
  }
  getEventCustomerValidationResponse(
    eventRegistrationId: number | string
  ): Observable<EventCustomerValidationModel[]> {
    return this.get<EventCustomerValidationModel[]>(
      'api/EventDetails/GetCustomerValidationInformation/' +
        eventRegistrationId,
      {}
    ) as Observable<EventCustomerValidationModel[]>;
  }
  getEventDocuments(eventRegistrationId: string | number) {
    return this.get<EventRegistrationDocumentModel>(
      `api/EventDetails/GetEventDocuments/${eventRegistrationId}`,
      {}
    ) as Observable<EventRegistrationDocumentModel>;
  }
  getCustomerAcknowledgementDocument(eventRegistrationId: string | number) {
    return this.get<CustomerAcknowledgementDocumentModel>(
      `api/EventDetails/GetCustomerAcnowledgementDocument/${eventRegistrationId}`,
      {}
    ) as Observable<CustomerAcknowledgementDocumentModel>;
  }
  getTaskOwnerDropDown(): Observable<EventTaskOwner[]> {
    return this.get<EventTaskOwner[]>(
      'api/EventDetails/GetAllTaskOwners/',
      {}
    ) as Observable<EventTaskOwner[]>;
  }

  getPMONextStepsDropDown(): Observable<EventPmoNextSteps[]> {
    return this.get<EventPmoNextSteps[]>(
      'api/EventDetails/GetAllPmoNextSteps/',
      {}
    ) as Observable<EventPmoNextSteps[]>;
  }

  getL1InternalStatusDropDown(): Observable<EventL1InternalStatus[]> {
    return this.get<EventL1InternalStatus[]>(
      'api/EventDetails/GetAllL1InternalStatus/',
      {}
    ) as Observable<EventL1InternalStatus[]>;
  }

  getL2InternalStatusDropDown(): Observable<EventL2InternalStatus[]> {
    return this.get<EventL2InternalStatus[]>(
      'api/EventDetails/GetAllL2InternalStatus/',
      {}
    ) as Observable<EventL2InternalStatus[]>;
  }

  updateCustomerSurveys(
    request: CustomerSurveySubmissionModel
  ): Observable<boolean> {
    return this.post<CustomerSurveySubmissionModel, boolean>(
      'api/EventDetails/UpdateCustomerSurveys',
      request,
      {}
    ) as Observable<boolean>;
  }

  getEventDocumentStatuses(): Observable<EventDocumentStatus[]> {
    return this.get<EventDocumentStatus[]>(
      'api/EventDetails/GetEventDocumentStatuses',
      {}
    ) as Observable<EventDocumentStatus[]>;
  }

  getCustomerAcknowledgementStatuses(): Observable<
    CustomerAcknowledgementStatus[]
  > {
    return this.get<CustomerAcknowledgementStatus[]>(
      'api/EventDetails/GetCustomerAcknowledgementStatuses',
      {}
    ) as Observable<CustomerAcknowledgementStatus[]>;
  }

  getEventDocumentRejectionReasons(): Observable<
    EventDocumentRejectionReason[]
  > {
    return this.get<EventDocumentRejectionReason[]>(
      'api/EventDetails/GetEventDocumentRejectionReasons',
      {}
    ) as Observable<EventDocumentRejectionReason[]>;
  }

  getCustomerAckRejectionReasonList(): Observable<
    CustomerAckRejectionReason[]
  > {
    return this.get<CustomerAckRejectionReason[]>(
      'api/EventDetails/GetCustomerAckRejectionReasons',
      {}
    ) as Observable<CustomerAckRejectionReason[]>;
  }

  updateEventDocumentsStatus(
    request: UpdateEventDocumentsStatusRequest
  ): Observable<boolean> {
    return this.post<UpdateEventDocumentsStatusRequest, boolean>(
      'api/EventDetails/UpdateEventDocumentsStatus',
      request,
      {}
    ) as Observable<boolean>;
  }

  updateCustomerAckDocumentsStatus(
    request: UpdateCustomerAckDocumentsStatusRequest
  ): Observable<boolean> {
    return this.post<UpdateCustomerAckDocumentsStatusRequest, boolean>(
      'api/EventDetails/UpdateCustomerAckDocumentsStatus',
      request,
      {}
    ) as Observable<boolean>;
  }

  getEventDetails(eventRegistrationId): Observable<EventDetails> {
    return this.get<EventDetails>(
      'api/EventDashboard/GetEventDetails/' + eventRegistrationId,
      {}
    ) as Observable<EventDetails>;
  }

  updateEventDetails(request: EventDetails): Observable<boolean> {
    return this.post<EventDetails, boolean>(
      'api/EventDetails/UpdateEventDetails',
      request,
      {}
    ) as Observable<boolean>;
  }

  getFileSasUrl(document: EventPoeDocument): Observable<string> {
    return this.post<EventPoeDocument, BlobFileSasUrl>(
      'api/EventDetails/DownloadFileSASUrl',
      document,
      {}
    ).pipe(map(s => s.blobUrl + s.sasToken)) as Observable<string>;
  }

  getCustomerValidationPreviewDetails(
    eventRegistrationId
  ): Observable<CustomerValidationPreviewEventDetails> {
    return this.get<CustomerValidationPreviewEventDetails>(
      'api/EventDetails/GetCustomerValidationPreviewDetails/' +
        eventRegistrationId,
      {}
    ) as Observable<CustomerValidationPreviewEventDetails>;
  }
  getCustomerSurveyPreviewDetails(
    eventRegistrationId
  ): Observable<CustomerSurveyPreviewEventDetails> {
    return this.get<CustomerSurveyPreviewEventDetails>(
      'api/EventDetails/GetCustomerSurveyPreviewDetails/' + eventRegistrationId,
      {}
    ) as Observable<CustomerSurveyPreviewEventDetails>;
  }

  getEventCustomersDropDown(eventRegistrationId): Observable<EventCustomers[]> {
    return this.get<EventCustomers[]>(
      'api/EventDetails/GetEventCustomers/' + eventRegistrationId,
      {}
    ) as Observable<EventCustomers[]>;
  }
  getEventAttendeeDropDown(
    eventRegistrationId: number,
    eventCustomerId: number
  ): Observable<EventCustomerAttendee[]> {
    return this.get<EventCustomerAttendee[]>(
      'api/EventDetails/GetCustomerAttendees?eventRegistrationId=' +
        eventRegistrationId +
        '&eventCustomerId=' +
        eventCustomerId,
      {}
    ) as Observable<EventCustomerAttendee[]>;
  }

  getCustomerValidationInformationByCustomer(
    eventRegistrationId
  ): Observable<CustomerValidationInfo> {
    return this.get<CustomerValidationInfo>(
      'api/EventDetails/GetCustomerValidationInformationByCustomer/' +
        eventRegistrationId,
      {}
    ) as Observable<CustomerValidationInfo>;
  }

  getCustomerRejectionReasonsDropDown(): Observable<
    EventCustomerRejectionReason[]
  > {
    return this.get<EventCustomerRejectionReason[]>(
      'api/EventDetails/GetCustomerRejectionReasons',
      {}
    ) as Observable<EventCustomerRejectionReason[]>;
  }

  getCustomerValidationStatusesDropDown(): Observable<
    EventCustomerValidationStatusDataItem[]
  > {
    return this.get<EventCustomerValidationStatusDataItem[]>(
      'api/EventDetails/GetCustomerValidationStatuses',
      {}
    ) as Observable<EventCustomerValidationStatusDataItem[]>;
  }

  updateCustomerValidationStatuses(
    updateCustomerValidationStatusesModel: EventCustomerValidationRequestModel
  ) {
    return this.post<EventCustomerValidationRequestModel, boolean>(
      'api/EventDetails/UpdateCustomerValidationStatuses',
      updateCustomerValidationStatusesModel,
      {}
    ) as Observable<boolean>;
  }

  getEventPOEStatusDropDown(): Observable<EventPOEStatus[]> {
    return this.get<EventPOEStatus[]>(
      'api/EventDetails/GetEventPOEStatuses',
      {}
    ) as Observable<EventPOEStatus[]>;
  }

  getEventPoeInformation(
    eventRegistrationId: number | string
  ): Observable<EventPOEInfoModel> {
    return this.get<EventPOEInfoModel>(
      'api/EventDetails/GetEventPoeInformation/' + eventRegistrationId,
      {}
    ) as Observable<EventPOEInfoModel>;
  }

  updateEventPoeInformation(updateEventPOEInfoModel: EventPOEInfoModel) {
    return this.post<EventPOEInfoModel, boolean>(
      'api/EventDetails/UpdateEventPoeInformation',
      updateEventPOEInfoModel,
      {}
    ) as Observable<boolean>;
  }

  getEventDocumentPreviewDetails(
    eventRegistrationId,
    documentId
  ): Observable<EventPreviewDetails> {
    return this.get<EventPreviewDetails>(
      'api/EventDetails/GetEventDocumentPreviewDetails?eventRegistrationId=' +
        eventRegistrationId +
        '&documentId=' +
        documentId,
      {}
    ) as Observable<EventPreviewDetails>;
  }

  getCustomerAckDocumentPreviewDetails(
    eventRegistrationId
  ): Observable<EventPreviewDetails> {
    return this.get<EventPreviewDetails>(
      'api/EventDetails/GetCustomerAckDocumentPreviewDetails?eventRegistrationId=' +
        eventRegistrationId,
      {}
    ) as Observable<EventPreviewDetails>;
  }

  getPreviewFileContent(documentModel: EventPoeDocument): any {
    return this.getFileSasUrl(documentModel).pipe(
      mergeMap(blolFileSasUrl =>
        this.http.get(blolFileSasUrl, { responseType: 'blob' })
      )
    ) as Observable<any>;
  }

  getRequiredCustomerLimit(
    eventRegistrationId
  ): Observable<EventRequiredCustomerLimit> {
    return this.get<EventRequiredCustomerLimit>(
      'api/EventDetails/GetRequiredCustomerLimit?eventId=' +
        eventRegistrationId,
      {}
    ) as Observable<EventRequiredCustomerLimit>;
  }

  uploadPOEDocToBlob(
    request: POEDocument,
    eventRegistrationId: string,
    subFolderName = ''
  ) {
    return this.post<POEDocument, ApiResponse>(
      'api/EventDetails/UploadPOEDocToAzureBlob?eventRegistrationId=' +
        eventRegistrationId +
        '&subFolderName=' +
        subFolderName,
      request,
      {}
    ) as Observable<ApiResponse>;
  }

  UploadPoeDocuments(request: ProgramDocument): Observable<boolean> {
    return this.post<ProgramDocument, boolean>(
      'api/EventDetails/UploadPoeDocuments/',
      request,
      {}
    ) as Observable<boolean>;
  }

  getAllMilestonePaymentStatuses(
    programTypeGuId: string
  ): Observable<MilestonePaymentStatus[]> {
    return this.get<MilestonePaymentStatus[]>(
      'api/EventDetails/GetAllMilestonePaymentStatuses/' + programTypeGuId,
      {}
    ) as Observable<MilestonePaymentStatus[]>;
  }

  getEventDeclineReasons(): Observable<DeclineReason[]> {
    return this.get<DeclineReason[]>(
      'api/EventDetails/GetEventDeclineReasons',
      {}
    ) as Observable<DeclineReason[]>;
  }

  getSurveyDetails(eventRegistrationId: number): Observable<SurveyDetails> {
    return this.get<SurveyDetails>(
      'api/EventDetails/GetSurveyDetails/' + eventRegistrationId,
      {}
    ) as Observable<SurveyDetails>;
  }

  getCustomerAcknowledgementFile(
    eventRegistrationId: string
  ): Observable<CustomerAcknowledgement[]> {
    return this.get<CustomerAcknowledgement[]>(
      'api/EventDetails/GetCustomerAcknowledgementFiles/' + eventRegistrationId,
      {}
    ) as Observable<CustomerAcknowledgement[]>;
  }

  updateCustomerAcknowledgement(request: ProgramDocument): Observable<boolean> {
    return this.post<ProgramDocument, boolean>(
      'api/EventDetails/UpdateCustomerAcknowledgement',
      request,
      {}
    ) as Observable<boolean>;
  }

  deleteFile(document: EventPoeDocument) {
    return this.post<EventPoeDocument, boolean>(
      'api/EventDetails/DeleteFile',
      document,
      {}
    ) as Observable<boolean>;
  }

  downloadOFTTemplate(document: EventPoeDocument): any {
    return this.post('api/EventDetails/DownloadOFTTemplate', document, {
      responseType: 'blob',
      reportProgress: true,
      observe: 'events'
    }) as Observable<any>;
  }
}
